import { useEffect, useState } from "react";
import { MessageCircle, X, RotateCcw } from "lucide-react";
import { indiaHub } from "@/lib/api";
import { useDraggablePosition } from "@/lib/useDraggablePosition";

import { log } from "@/lib/log";
// Floating WhatsApp channel widget — visible on every public page.
// The channel URL is editable in /admin/settings (admin-only) so we don't
// hardcode the placeholder.
// Draggable: persists per-user (DB) + localStorage cache.
// z-index intentionally tops the "Made with Emergent" preview badge so the
// WhatsApp CTA always remains tappable.
export default function WhatsAppFloat() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const { offset, dragProps, reset, moved } = useDraggablePosition("whatsapp");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect a session-level dismiss (per tab only — don't permanently hide).
    if (window.sessionStorage?.getItem("rp_whatsapp_dismissed_v1")) {
      setHidden(true);
    }
    indiaHub.whatsappChannel().then(setData).catch(() => {});
  }, []);

  if (hidden || !data?.active || !data?.url) return null;

  const dismiss = () => {
    setHidden(true);
    try { window.sessionStorage?.setItem("rp_whatsapp_dismissed_v1", "1"); } catch (err) {
      log.warn("[WhatsAppFloat] could not persist dismiss:", err?.message);
    }
  };

  // The anchor is fixed bottom-4 right-4. The drag offset is applied via
  // translate3d so the widget can be moved anywhere on screen.
  return (
    <div
      className="fixed bottom-4 right-4 z-[10000]"
      style={{ transform: `translate3d(${-offset.x}px, ${-offset.y}px, 0)` }}
      data-testid="whatsapp-float"
    >
      <div className="relative group">
        {moved && (
          <button
            type="button"
            onClick={reset}
            data-no-drag
            className="absolute -top-2 -left-2 z-10 rounded-full bg-rp-bg border border-rp-border p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Reset WhatsApp widget position"
            data-testid="whatsapp-float-reset"
          >
            <RotateCcw className="h-3 w-3 text-rp-textFaint" />
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          data-no-drag
          className="absolute -top-2 -right-2 z-10 rounded-full bg-rp-bg border border-rp-border p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Dismiss WhatsApp widget"
          data-testid="whatsapp-float-dismiss"
        >
          <X className="h-3 w-3 text-rp-textFaint" />
        </button>
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer"
          {...dragProps}
          className="flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FA855] text-white px-4 py-3 shadow-lg shadow-emerald-900/30 font-semibold text-sm transition-colors select-none"
          data-testid="whatsapp-float-link"
        >
          <MessageCircle className="h-5 w-5" />
          Join: India Risk Alerts
        </a>
      </div>
    </div>
  );
}
